"use client";

import type { DealType } from "@/lib/dealTypes";
import {
  estimateFinancedPrincipal,
  estimateTotalMonthlyPayment,
  formatMoneyBrief,
  type FinancingFormFields,
} from "@/lib/financingTerms";
import type { ListingFormFieldError } from "@/lib/listing-form";
import {
  FieldError,
  FieldGrid,
  FieldLabel,
  TextInput,
  TextTextarea,
} from "./ListingFormFields";
import { listingFormStyles as s } from "./listingFormStyles";
import { theme } from "@/lib/uiTheme";

type Props = {
  dealType: DealType | "";
  values: FinancingFormFields;
  errors: ListingFormFieldError[];
  currency: string;
  disabled?: boolean;
  onChange: <K extends keyof FinancingFormFields>(
    field: K,
    value: FinancingFormFields[K],
  ) => void;
};

type Field = {
  name: Exclude<
    keyof FinancingFormFields,
    "due_on_sale_acknowledged" | "interest_rate_is_zero"
  >;
  label: string;
  type?: "date" | "textarea";
  required?: boolean;
};

const FIELDS: Record<DealType, Field[]> = {
  cash: [],
  double_close: [],
  fsbo: [],
  other: [],
  subject_to: [
    {
      name: "existing_loan_balance",
      label: "Existing loan balance",
      required: true,
    },
    { name: "existing_interest_rate", label: "Existing interest rate" },
    {
      name: "monthly_principal_interest",
      label: "Monthly principal and interest",
    },
    { name: "monthly_taxes", label: "Monthly taxes" },
    { name: "monthly_insurance", label: "Monthly insurance" },
    { name: "hoa_payment", label: "Monthly HOA payment" },
    {
      name: "total_monthly_payment",
      label: "Total monthly payment",
      required: true,
    },
    {
      name: "cash_needed_at_closing",
      label: "Cash needed at closing",
      required: true,
    },
    {
      name: "remaining_loan_term_months",
      label: "Remaining loan term (months)",
    },
    { name: "loan_maturity_date", label: "Loan maturity date", type: "date" },
    { name: "arrears_amount", label: "Arrears amount" },
    { name: "occupancy_status", label: "Occupancy status" },
    { name: "servicing_notes", label: "Servicing notes", type: "textarea" },
  ],
  seller_finance: [
    { name: "purchase_price", label: "Purchase price" },
    { name: "down_payment", label: "Down payment", required: true },
    { name: "monthly_payment", label: "Monthly payment", required: true },
    { name: "interest_rate", label: "Interest rate", required: true },
    { name: "amortization_months", label: "Amortization (months)" },
    { name: "balloon_term_months", label: "Balloon term (months)" },
    { name: "balloon_due_date", label: "Balloon due date", type: "date" },
    { name: "first_payment_date", label: "First payment date", type: "date" },
    { name: "lien_position", label: "Lien position" },
    { name: "prepayment_terms", label: "Prepayment terms", type: "textarea" },
  ],
  lease_option: [
    { name: "option_consideration", label: "Option consideration" },
    {
      name: "monthly_lease_payment",
      label: "Monthly lease payment",
      required: true,
    },
    {
      name: "option_purchase_price",
      label: "Option purchase price",
      required: true,
    },
    {
      name: "option_term_months",
      label: "Option term (months)",
      required: true,
    },
    { name: "rent_credit", label: "Rent credit" },
    {
      name: "maintenance_responsibility",
      label: "Maintenance responsibility",
      type: "textarea",
    },
    { name: "taxes_responsibility", label: "Taxes responsibility" },
    { name: "insurance_responsibility", label: "Insurance responsibility" },
    { name: "additional_terms", label: "Additional terms", type: "textarea" },
  ],
  wraparound_mortgage: [
    {
      name: "underlying_balance",
      label: "Existing underlying balance",
      required: true,
    },
    { name: "underlying_interest_rate", label: "Underlying interest rate" },
    {
      name: "underlying_monthly_payment",
      label: "Underlying monthly payment",
      required: true,
    },
    {
      name: "wrap_purchase_price",
      label: "Wrap purchase price",
      required: true,
    },
    { name: "wrap_principal", label: "Wrap principal" },
    { name: "down_payment", label: "Down payment" },
    { name: "wrap_interest_rate", label: "Wrap interest rate" },
    {
      name: "wrap_monthly_payment",
      label: "Buyer wrap payment",
      required: true,
    },
    {
      name: "servicing_arrangement",
      label: "Servicing arrangement",
      required: true,
      type: "textarea",
    },
  ],
  hybrid_creative: [
    {
      name: "structure_description",
      label: "Deal structure",
      required: true,
      type: "textarea",
    },
    {
      name: "cash_needed_at_closing",
      label: "Cash needed at closing",
      required: true,
    },
    { name: "monthly_payment", label: "Monthly payment" },
    { name: "interest_rate", label: "Interest rate" },
    { name: "additional_terms", label: "Additional terms", type: "textarea" },
  ],
  assignment: [
    { name: "assignment_fee", label: "Assignment fee" },
    { name: "earnest_money", label: "Earnest money" },
    { name: "closing_deadline", label: "Closing deadline", type: "date" },
    {
      name: "structure_description",
      label: "Assignment notes",
      type: "textarea",
    },
  ],
};

function errorFor(errors: ListingFormFieldError[], field: string) {
  return errors.find((error) => error.field === field)?.message;
}

export default function FinancingFields({
  dealType,
  values,
  errors,
  currency,
  disabled,
  onChange,
}: Props) {
  if (!dealType || !FIELDS[dealType].length) return null;
  const estimatedPrincipal = estimateFinancedPrincipal(
    values.purchase_price,
    values.down_payment,
  );
  const estimatedPayment = estimateTotalMonthlyPayment(values);

  return (
    <section style={{ ...s.panel, marginTop: 20 }}>
      <h3 style={s.sectionTitle}>Financing terms</h3>
      <p style={s.sectionHint}>
        Add the terms buyers need to understand this deal structure.
      </p>
      <FieldGrid>
        {FIELDS[dealType].map(({ name, label, type, required }) => {
          const error = errorFor(errors, name);
          const id = `listing-financing-${name}`;
          return (
            <div key={name} style={s.field}>
              <FieldLabel htmlFor={id} required={required} optional={!required}>
                {label}
              </FieldLabel>
              {type === "textarea" ? (
                <TextTextarea
                  id={id}
                  name={name}
                  rows={2}
                  value={values[name]}
                  disabled={disabled}
                  error={Boolean(error)}
                  onChange={(event) => onChange(name, event.target.value)}
                />
              ) : (
                <TextInput
                  id={id}
                  name={name}
                  type={type}
                  inputMode={type ? undefined : "decimal"}
                  value={values[name]}
                  disabled={disabled}
                  error={Boolean(error)}
                  onChange={(event) => onChange(name, event.target.value)}
                />
              )}
              <FieldError message={error} />
            </div>
          );
        })}
      </FieldGrid>

      {(dealType === "seller_finance" || dealType === "subject_to") && (
        <div style={{ ...s.sectionHint, marginTop: 16 }}>
          {estimatedPrincipal != null ? (
            <div>
              Estimated financed principal:{" "}
              {formatMoneyBrief(estimatedPrincipal, currency)}
            </div>
          ) : null}
          {estimatedPayment != null ? (
            <div>
              Estimated total payment:{" "}
              {formatMoneyBrief(estimatedPayment, currency)} per month
            </div>
          ) : null}
        </div>
      )}

      {dealType === "seller_finance" ? (
        <label style={{ ...s.checkboxRow, marginTop: 16 }}>
          <input
            id="listing-financing-interest_rate_is_zero"
            type="checkbox"
            checked={values.interest_rate_is_zero}
            disabled={disabled}
            onChange={(event) =>
              onChange("interest_rate_is_zero", event.target.checked)
            }
            style={{ accentColor: theme.colors.primary }}
          />
          <span style={{ fontSize: 14, color: theme.colors.ink }}>
            This seller-financed note has a 0% interest rate.
          </span>
        </label>
      ) : null}

      {dealType === "subject_to" ? (
        <div style={{ marginTop: 16 }}>
          <label style={s.checkboxRow}>
            <input
              id="listing-financing-due_on_sale_acknowledged"
              type="checkbox"
              checked={values.due_on_sale_acknowledged}
              disabled={disabled}
              onChange={(event) =>
                onChange("due_on_sale_acknowledged", event.target.checked)
              }
              style={{ accentColor: theme.colors.primary }}
            />
            <span
              style={{ fontSize: 14, color: theme.colors.ink, lineHeight: 1.5 }}
            >
              I acknowledge that an existing loan may contain a due-on-sale
              clause and have disclosed this deal structure accurately.
            </span>
          </label>
          <FieldError message={errorFor(errors, "due_on_sale_acknowledged")} />
        </div>
      ) : null}
    </section>
  );
}
